/**
 * Terrain shaders: continuous distance-dependent LOD (CDLOD) with vertex-shader geomorphing.
 *
 * One grid patch mesh is instanced across the whole theatre, and each instance carries only its
 * origin, its size and its LOD level. Heights come from a texture sampled in the *vertex* shader,
 * so no terrain mesh is ever built on the CPU, nothing is rebuilt when the camera moves, and the
 * entire 24 km theatre is one draw call.
 *
 * Geomorphing is the part that is not optional (M3 notes). Each vertex slides toward the position
 * it would occupy in the parent LOD as the patch approaches its range limit, so by the time a
 * patch is swapped for its coarser parent the two are geometrically identical. That removes both
 * the pop and — because neighbouring levels agree along their shared edge — the cracks, without
 * skirts or stitching.
 */

/** Shared between the terrain and ocean fragment shaders. */
export const HAZE_CHUNK = /* glsl */ `
uniform vec3 uHazeColour;
uniform float uHazeDensity;

/** Exponential-squared haze. Also the draw-distance mechanism (SPEC §3.3.2). */
vec3 applyHaze(vec3 colour, float distanceM) {
  float d = uHazeDensity * distanceM;
  float factor = 1.0 - exp(-d * d);
  return mix(colour, uHazeColour, clamp(factor, 0.0, 1.0));
}
`;

export const TERRAIN_VERTEX = /* glsl */ `
precision highp float;

attribute vec4 aPatch; // originX, originZ, size, lodLevel

uniform sampler2D uHeightMap;
uniform vec2 uWorldOrigin;
uniform float uWorldSize;
uniform float uHeightScale;
uniform float uHeightOffset;
uniform float uGridDim;
uniform vec3 uCameraPosition;
uniform vec2 uMorphRanges[8]; // start, end per LOD level

varying vec3 vWorldPosition;
varying float vHeight;
varying float vViewDistance;

float sampleHeight(vec2 worldXZ) {
  vec2 uv = (worldXZ - uWorldOrigin) / uWorldSize;
  return texture2D(uHeightMap, clamp(uv, 0.0, 1.0)).r * uHeightScale + uHeightOffset;
}

void main() {
  // The patch mesh is a unit grid in the XZ plane; position.xz runs 0..1.
  vec2 grid = position.xz;
  vec2 worldXZ = aPatch.xy + grid * aPatch.z;

  // Morph factor from the *unmorphed* position: using the morphed one feeds back on itself and
  // the surface shimmers as the camera moves.
  float viewDistance = distance(uCameraPosition, vec3(worldXZ.x, sampleHeight(worldXZ), worldXZ.y));
  int level = int(aPatch.w + 0.5);
  vec2 range = uMorphRanges[level];
  float morphK = clamp((viewDistance - range.x) / max(range.y - range.x, 1.0), 0.0, 1.0);

  // Slide odd-indexed vertices onto their even neighbours: at morphK = 1 the patch is exactly
  // the parent grid, which is what makes the level switch invisible.
  vec2 fracPart = fract(grid * uGridDim * 0.5) * 2.0 / uGridDim;
  vec2 morphedGrid = grid - fracPart * morphK;
  worldXZ = aPatch.xy + morphedGrid * aPatch.z;

  float height = sampleHeight(worldXZ);
  vec3 worldPosition = vec3(worldXZ.x, height, worldXZ.y);

  vWorldPosition = worldPosition;
  vHeight = height;
  vViewDistance = distance(uCameraPosition, worldPosition);

  gl_Position = projectionMatrix * viewMatrix * vec4(worldPosition, 1.0);
}
`;

export const TERRAIN_FRAGMENT = /* glsl */ `
precision highp float;

uniform sampler2D uHeightMap;
uniform sampler2D uDetailMap;
uniform vec2 uWorldOrigin;
uniform float uWorldSize;
uniform float uHeightScale;
uniform float uHeightOffset;
uniform float uTexelWorldM;

uniform vec3 uSunDirection;
uniform vec3 uSunColour;
uniform vec3 uAmbientSky;
uniform vec3 uAmbientGround;

uniform vec3 uRockColour;
uniform vec3 uScrubColour;
uniform vec3 uSandColour;
uniform vec3 uHighColour;
uniform float uDetailNearScale;
uniform float uDetailFarScale;

varying vec3 vWorldPosition;
varying float vHeight;
varying float vViewDistance;

${HAZE_CHUNK}

float sampleHeightAt(vec2 worldXZ) {
  vec2 uv = (worldXZ - uWorldOrigin) / uWorldSize;
  return texture2D(uHeightMap, clamp(uv, 0.0, 1.0)).r * uHeightScale + uHeightOffset;
}

void main() {
  // Normals from the heightfield rather than from interpolated vertex normals: the mesh changes
  // resolution continuously under geomorphing, and shading that followed it would swim.
  float step = uTexelWorldM;
  float hL = sampleHeightAt(vWorldPosition.xz - vec2(step, 0.0));
  float hR = sampleHeightAt(vWorldPosition.xz + vec2(step, 0.0));
  float hD = sampleHeightAt(vWorldPosition.xz - vec2(0.0, step));
  float hU = sampleHeightAt(vWorldPosition.xz + vec2(0.0, step));
  vec3 normal = normalize(vec3(hL - hR, 2.0 * step, hD - hU));

  float slope = 1.0 - clamp(normal.y, 0.0, 1.0);

  vec4 detailNear = texture2D(uDetailMap, vWorldPosition.xz * uDetailNearScale);
  vec4 detailFar = texture2D(uDetailMap, vWorldPosition.xz * uDetailFarScale);
  // The large-scale term is what defeats visible tiling of the small one (SPEC §5.2).
  float variation = mix(0.72, 1.28, detailFar.g);

  // Splat by slope and altitude: sand at the waterline, scrub on the flats, rock on anything
  // steep, and bare stone on the tops.
  float sand = 1.0 - smoothstep(1.5, 14.0, vHeight);
  float rock = smoothstep(0.28, 0.55, slope + detailNear.r * 0.12);
  float high = smoothstep(680.0, 1050.0, vHeight);

  vec3 albedo = mix(uScrubColour, uSandColour, sand);
  albedo = mix(albedo, uRockColour, rock);
  albedo = mix(albedo, uHighColour, high * (1.0 - sand));
  albedo *= variation * mix(0.86, 1.14, detailNear.b);

  float lambert = max(dot(normal, uSunDirection), 0.0);
  // A wrapped term stands in for bounce light, so shadowed slopes read as shape rather than
  // silhouette. Cascaded shadows arrive at M10; nothing here casts yet.
  float wrapped = max((dot(normal, uSunDirection) + 0.35) / 1.35, 0.0);
  vec3 ambient = mix(uAmbientGround, uAmbientSky, normal.y * 0.5 + 0.5);
  vec3 colour = albedo * (ambient + uSunColour * mix(lambert, wrapped, 0.35));

  gl_FragColor = vec4(applyHaze(colour, vViewDistance), 1.0);
}
`;
